import React from 'react';
import { TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

/**
 * AuditDeltaBreakdown — explains the overall score delta vs the user's
 * previous audit by listing per-category and per-pillar deltas. Renders
 * nothing if there is no previous audit to compare against.
 */
const CATEGORY_LABELS = {
  headline: 'Headline',
  about: 'About / Summary',
  experience: 'Experience',
  skills: 'Skills',
  engagement_hooks: 'Engagement hooks',
};

const PILLAR_LABELS = {
  discoverability: 'Discoverability',
  positioning: 'Positioning',
  credibility: 'Credibility',
};

const tone = (delta) => {
  if (delta > 0) return {
    cls: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/20',
    Icon: TrendingUp,
    sign: '+',
  };
  if (delta < 0) return {
    cls: 'text-destructive',
    bg: 'bg-destructive/10 border-destructive/20',
    Icon: TrendingDown,
    sign: '',
  };
  return {
    cls: 'text-muted-foreground',
    bg: 'bg-secondary/60 border-border',
    Icon: Minus,
    sign: '±',
  };
};

const fmtDate = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_e) { return ''; }
};

export function AuditDeltaBreakdown({ analysis, overallDelta, prevScore, currentScore, prevDateLabel }) {
  const breakdown = analysis?.previous_breakdown;
  if (!breakdown) return null;
  const cats = (breakdown.categories || []).filter((c) => c.delta !== 0);
  const pillars = (breakdown.pillars || []).filter((p) => p.delta !== 0);
  // If absolutely nothing moved we still want to show the panel with the
  // "no movement" state because the user explicitly asked WHY a dip happened.
  const hasAnyData = (breakdown.categories || []).length || (breakdown.pillars || []).length;
  if (!hasAnyData) return null;

  const drops = cats.filter((c) => c.delta < 0);
  const gains = cats.filter((c) => c.delta > 0);
  const isOverallDown = typeof overallDelta === 'number' && overallDelta < 0;

  return (
    <Card
      className="border-border overflow-hidden mt-3"
      data-testid="audit-delta-breakdown"
    >
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
              What changed
            </p>
            <p className="mt-0.5 font-display text-base font-semibold tracking-[-0.01em]">
              {isOverallDown
                ? `Why your overall dipped by ${Math.abs(overallDelta)}`
                : overallDelta > 0
                ? `What pushed you up by ${overallDelta}`
                : 'No change since your previous audit'}
            </p>
          </div>
          {prevDateLabel && (
            <Badge variant="outline" className="rounded-full text-[10px] font-mono-edge tracking-[0.18em] uppercase">
              vs. {prevDateLabel} &nbsp;·&nbsp; {prevScore} → {currentScore}
            </Badge>
          )}
        </div>

        {/* Categories grid */}
        {(breakdown.categories || []).length > 0 && (
          <>
            <p className="mt-4 text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
              Categories
            </p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="audit-delta-categories">
              {(breakdown.categories || []).map((c) => {
                const t = tone(c.delta);
                const Icon = t.Icon;
                return (
                  <div
                    key={c.key}
                    className={`rounded-xl border ${t.bg} p-2.5 flex items-center justify-between gap-2`}
                    data-testid={`audit-delta-cat-${c.key}`}
                  >
                    <div className="min-w-0">
                      <p className="text-[12px] font-medium truncate">
                        {CATEGORY_LABELS[c.key] || c.key}
                      </p>
                      <p className="text-[11px] text-muted-foreground tabular-nums">{c.previous} → {c.current}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 ${t.cls} font-semibold text-sm tabular-nums shrink-0`}>
                      <Icon size={13} aria-hidden="true" />
                      {t.sign}{c.delta}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pillars row (if available) */}
        {(breakdown.pillars || []).length > 0 && (
          <>
            <p className="mt-4 text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
              Pillars
            </p>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="audit-delta-pillars">
              {(breakdown.pillars || []).map((p) => {
                const t = tone(p.delta);
                const Icon = t.Icon;
                return (
                  <div
                    key={p.key}
                    className={`rounded-xl border ${t.bg} p-2.5 flex items-center justify-between gap-2`}
                    data-testid={`audit-delta-pillar-${p.key}`}
                  >
                    <div className="min-w-0">
                      <p className="text-[12px] font-medium truncate">{PILLAR_LABELS[p.key] || p.key}</p>
                      <p className="text-[11px] text-muted-foreground tabular-nums">{p.previous} → {p.current}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 ${t.cls} font-semibold text-sm tabular-nums shrink-0`}>
                      <Icon size={13} aria-hidden="true" />
                      {t.sign}{p.delta}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Quick takeaway */}
        {(drops.length > 0 || gains.length > 0) && (
          <p className="mt-4 text-[12px] text-muted-foreground leading-relaxed flex items-start gap-2">
            <Sparkles size={13} className="mt-0.5 text-primary shrink-0" aria-hidden="true" />
            <span>
              {drops.length > 0 && (
                <>
                  Biggest drop:{' '}
                  <strong className="text-foreground">
                    {CATEGORY_LABELS[drops.sort((a, b) => a.delta - b.delta)[0].key] || drops[0].key}
                  </strong>
                  {gains.length > 0 ? ' · ' : '. '}
                </>
              )}
              {gains.length > 0 && (
                <>
                  Biggest gain:{' '}
                  <strong className="text-foreground">
                    {CATEGORY_LABELS[gains.sort((a, b) => b.delta - a.delta)[0].key] || gains[0].key}
                  </strong>
                  .
                </>
              )}
            </span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
